import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiX, HiCheck, HiPlus, HiChevronDown, HiArrowRight } from "react-icons/hi";
import { useAuth } from "../../../contexts/AuthContext";
import {
  ActivityLog,
  ActivityMeasurement,
  NiggleLog,
  PerformanceActivity,
  PerformanceLog,
  Session,
} from "../../../types/post";
import SessionActivitiesTable from "../../sessions/SessionActivitiesTable";
import { formatMeasurementWithUnit } from "../../../utils/unitUtils";
import { SessionNotes } from "../../sessions/SessionNotesMarkdown";
import AddSessionDetailsModal from "../../sessions/AddSessionDetailsModal";
import AddPerformanceLogForm from "../AddPerformanceLogForm";
import { useActivityNameResolver } from "../../../hooks/useActivityNameResolver";
import {
  addTrainingLog,
  addLogIDToDayPost,
  updateTrainingLog,
  addNiggleLog,
  addPerformanceLog,
  updatePerformanceLog,
  updateNiggleLog,
  linkActivityToTraining,
  getDayPostReferences,
  getNiggleLogs,
  getPerformanceLogs,
  getPerformanceLogsForDateRange,
  getSessions,
  getActivityLogs,
} from "../../../services/dayPostService";
import { sessionService } from "../../../services/sessionService";
import {
  getLinkedSessionIDs,
  getPrimarySessionID,
} from "../../../utils/sessionLinks";
import {
  getPrimaryTypeOptions,
  orderLinkedSessionIDs,
  requiresPrimarySelection,
  sumSessionDurationMinutes,
  PrimaryTypeOption,
} from "../../../utils/sessionRules";
import {
  TrainingType,
  calcRpe,
  parseDurationToMinutes,
  calcLoadModifier,
  calcLoad,
  convertDateIDToYYYYMMDD,
  fetchTrainingTypesForUser,
  resolveTrainingTypeForLog,
} from "./trainingLogUtils";
import Slider, {
  MUSCULAR_DESCRIPTIONS,
  RESPIRATION_DESCRIPTIONS,
} from "./Slider";
import DurationQuickChips from "./DurationQuickChips";
import PainInjurySection from "./PainInjurySection";
import { Sport } from "../../../types/activity";
import { trainingTypeService } from "../../../services/trainingTypeService";
import { getUserPublicData } from "../../../services/userService";
import { CustomTrainingType } from "../../../types/user";

// Context passed in from whichever surface opens the form. Everything except
// dateID is optional; contextual triggers (session, wearable activity) supply
// the extras so the new log is pre-linked on save.
export interface TrainingLogContext {
  dateID: string; // YYYYMMDD
  sessionId?: string; // existing session to link
  sessionMeasurements?: ActivityMeasurement[]; // measurements from a session being completed
  activityLogId?: string; // wearable activity to link
}

// Existing training log fields used to pre-populate the form when editing.
export interface TrainingLogInitialData {
  dateID?: string; // the day the log belongs to (YYYYMMDD)
  muscular: number;
  respiratory: number;
  duration: number;
  notes?: string;
  tag?: string[];
  trainingTypeID?: string;
  trainingPerformed?: number;
  pain?: boolean;
  injured?: boolean;
  linkedSessionIDs?: string[];
  linkedSessionID?: string;
  linkedActivityLogID?: string;
}

interface TrainingLogFormProps {
  open: boolean;
  onClose: () => void;
  context: TrainingLogContext;
  onSaved?: (trainingLogId: string) => void;
  // Edit mode: when set, the form pre-populates and updates the existing log
  // instead of creating a new one.
  editMode?: boolean;
  logId?: string;
  initialData?: TrainingLogInitialData;
}

// Performance payload captured from AddPerformanceLogForm (same inputs as the
// real performance log), held until the training log is saved.
type PendingPerformance = {
  notes: string;
  performanceExpectation: number;
  performanceResult: string;
  performanceTitle: string;
  performanceDate: string;
  activity?: PerformanceActivity;
  tag?: string[];
};

// Derive a short "name" + "value" summary from a performance log so the linked
// performance chip / preview can surface the actual result at a glance.
const summarizePerformance = (p: {
  performanceTitle?: string;
  performanceResult?: string;
  activity?: PerformanceActivity;
}): { name: string; value: string | null } => {
  const name =
    p.performanceTitle?.trim() ||
    p.activity?.activityName?.trim() ||
    "Performance";

  // Prefer measured activity variables (e.g. "5 km · 20 min"); fall back to the
  // free-text result when no activity measurements exist.
  const measured = (p.activity?.variables || [])
    .filter((v) => v.measurement !== null && v.measurement !== undefined)
    .map((v) => formatMeasurementWithUnit(v.measurement, v.unit, v.name));

  let value: string | null = null;
  if (measured.length > 0) {
    value = measured.join(" · ");
  } else if (p.performanceResult?.trim()) {
    value = p.performanceResult.trim();
  }
  return { name, value };
};

// Human-friendly name + key metric rows for a wearable activity log.
const summarizeActivity = (
  a: ActivityLog,
): { name: string; metrics: { label: string; value: string }[] } => {
  const name = a.sessionName?.trim() || a.provider || "Wearable activity";
  const metrics: { label: string; value: string }[] = [];
  if (a.duration != null)
    metrics.push({ label: "Duration", value: `${Math.round(a.duration)} min` });
  if (a.calories != null)
    metrics.push({ label: "Calories", value: `${Math.round(a.calories)} kcal` });
  if (a.aveHR != null)
    metrics.push({ label: "Avg HR", value: `${Math.round(a.aveHR)} bpm` });
  if (a.maxHR != null)
    metrics.push({ label: "Max HR", value: `${Math.round(a.maxHR)} bpm` });
  if (a.minHR != null)
    metrics.push({ label: "Min HR", value: `${Math.round(a.minHR)} bpm` });
  return { name, metrics };
};

// A floating-label wrapper so labels sit inside the field (select / input /
// textarea) instead of above it.
const FloatingField: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, required, children }) => (
  <div className="relative">
    <label className="absolute left-4 top-2 text-[11px] font-medium uppercase tracking-wider text-base-content/50 pointer-events-none z-10">
      {label}
      {required && <span className="text-error"> *</span>}
    </label>
    {children}
  </div>
);

// Chip representing a linked log. Activated (filled) when it holds data;
// tapping opens its editor to link / edit it.
const LinkedChip: React.FC<{
  label: string;
  active: boolean;
  open: boolean;
  onClick: () => void;
}> = ({ label, active, open, onClick }) => {
  // Three visually distinct states:
  //  • linked (active)  → solid filled pill + check  ("this log is attached")
  //  • not linked       → dashed outline + plus      ("tap to add")
  //  • open (expanded)  → ring + chevron rotates up   ("its editor is showing")
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-expanded={open}
      className={`group inline-flex items-center gap-1.5 pl-2.5 pr-2 py-1.5 rounded-full text-sm font-medium border transition-all ${
        active
          ? "bg-neutral text-white border-neutral shadow-sm"
          : "bg-base-100 text-base-content/60 border-dashed border-base-300 hover:border-base-content/40 hover:text-base-content/80"
      } ${
        open
          ? active
            ? "ring-2 ring-neutral/40 ring-offset-1 ring-offset-base-100"
            : "ring-2 ring-base-content/20"
          : ""
      }`}
    >
      <span
        className={`inline-flex items-center justify-center w-4 h-4 rounded-full ${
          active ? "bg-white/20" : ""
        }`}
      >
        {active ? (
          <HiCheck className="w-3 h-3" />
        ) : (
          <HiPlus className="w-3 h-3" />
        )}
      </span>
      <span>{label}</span>
      <HiChevronDown
        className={`w-3.5 h-3.5 transition-transform duration-200 ${
          active ? "opacity-80" : "opacity-40"
        } ${open ? "rotate-180" : ""}`}
      />
    </button>
  );
};

const EditorPanel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-md border border-base-300 bg-base-200/30 p-4 space-y-4">
    {children}
  </div>
);

// List of existing same-day logs the athlete can link. Selecting one toggles
// the selection; the parent decides how it maps to a link on save.
// Single-select by default; `multiple` lets several items be selected (used
// by the session field, plan 3a) and reports each toggle via `onToggle`.
const ExistingLogPicker: React.FC<{
  items: { id: string; label: string; sublabel?: string }[];
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  multiple?: boolean;
  selectedIds?: string[];
  onToggle?: (id: string) => void;
  title?: string;
}> = ({
  items,
  selectedId = null,
  onSelect,
  multiple = false,
  selectedIds = [],
  onToggle,
  title = "Link an existing log",
}) => {
  if (items.length === 0) return null;
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-base-content/50 font-grotesk">
        {title}
      </p>
      {items.map((item) => {
        const selected = multiple
          ? selectedIds.includes(item.id)
          : selectedId === item.id;
        return (
          <button
            key={item.id}
            type="button"
            role={multiple ? "checkbox" : undefined}
            aria-checked={multiple ? selected : undefined}
            aria-pressed={multiple ? undefined : selected}
            onClick={() =>
              multiple
                ? onToggle?.(item.id)
                : onSelect?.(selected ? null : item.id)
            }
            className={`w-full text-left text-sm py-2.5 px-3 rounded-xl border flex items-center justify-between gap-2 transition-colors ${
              selected
                ? "border-neutral bg-neutral/10"
                : "border-base-300 bg-base-100 hover:border-base-content/40"
            }`}
          >
            <span className="min-w-0">
              <span className="block truncate font-medium">{item.label}</span>
              {item.sublabel && (
                <span className="block truncate text-xs text-base-content/50">
                  {item.sublabel}
                </span>
              )}
            </span>
            {multiple ? (
              <span
                className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${
                  selected
                    ? "bg-neutral border-neutral text-neutral-content"
                    : "border-base-300"
                }`}
              >
                {selected && <HiCheck className="w-3.5 h-3.5" />}
              </span>
            ) : (
              selected && (
                <HiCheck className="w-4 h-4 text-neutral flex-shrink-0" />
              )
            )}
          </button>
        );
      })}
    </div>
  );
};

// Removable chips for the sessions currently linked to the log (plan 3a).
const LinkedSessionChips: React.FC<{
  sessions: { id: string; label: string }[];
  primaryId: string | null;
  onRemove: (id: string) => void;
}> = ({ sessions, primaryId, onRemove }) => {
  if (sessions.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {sessions.map((session) => {
        const isPrimary = sessions.length > 1 && session.id === primaryId;
        return (
          <span
            key={session.id}
            className={`inline-flex items-center gap-1 pl-3 pr-1 min-h-9 rounded-full text-xs font-grotesk border ${
              isPrimary
                ? "bg-neutral text-neutral-content border-neutral"
                : "bg-base-100 text-base-content border-base-300"
            }`}
          >
            <span className="truncate max-w-[9rem] sm:max-w-[14rem]">{session.label}</span>
            {isPrimary && (
              <span className="text-[10px] uppercase tracking-wider opacity-70">
                primary
              </span>
            )}
            <button
              type="button"
              onClick={() => onRemove(session.id)}
              aria-label={`Remove ${session.label}`}
              className={`w-8 h-8 -my-1 rounded-full flex items-center justify-center ${
                isPrimary ? "hover:bg-neutral-content/20" : "hover:bg-base-200"
              }`}
            >
              <HiX className="w-3 h-3" />
            </button>
          </span>
        );
      })}
    </div>
  );
};

// Decision 6: with more than one linked session the athlete must choose which
// session's training type is the log's primary type — no silent default.
const PrimaryTypePicker: React.FC<{
  options: PrimaryTypeOption[];
  selectedSessionID: string | null;
  onSelect: (sessionID: string) => void;
}> = ({ options, selectedSessionID, onSelect }) => (
  <div className="space-y-2">
    <p className="text-xs font-medium text-base-content/70 font-grotesk">
      Primary training type <span className="text-error">*</span>
    </p>
    <p className="text-xs text-base-content/50 font-grotesk">
      Pick the type this log counts as. That session becomes the primary one.
    </p>
    <div className="flex flex-wrap gap-2" role="radiogroup">
      {options.map((option) => {
        const selected = option.sessionID === selectedSessionID;
        return (
          <button
            key={option.sessionID}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onSelect(option.sessionID)}
            className={`px-4 min-h-11 rounded-full text-sm border transition-colors font-grotesk font-medium max-w-full truncate ${
              selected
                ? "bg-neutral text-neutral-content border-neutral"
                : "bg-base-100 text-base-content/80 border-base-300 hover:border-base-content/40"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  </div>
);

// Single-select sport filter above the training-type picker.
const SportFilterPicker: React.FC<{
  options: { value: string; label: string }[];
  selected: string;
  onSelect: (value: string) => void;
}> = ({ options, selected, onSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedLabel =
    options.find((o) => o.value === selected)?.label ?? "All sports";

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div
        className="relative border border-base-300 rounded-md bg-base-200 cursor-pointer"
        onClick={() => setOpen((o) => !o)}
      >
        <label className="absolute left-4 top-1 text-xs text-base-content/60 font-grotesk pointer-events-none z-10">
          Sport
        </label>
        <div className="flex items-center gap-2 pl-4 pr-11 pt-6 pb-2 min-h-[3.5rem]">
          <span className="text-sm font-geist text-base-content">
            {selectedLabel}
          </span>
        </div>
        <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
          <HiChevronDown
            className={`w-5 h-5 text-base-content/50 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-md shadow-lg overflow-hidden">
          <div className="max-h-60 overflow-y-auto">
            {options.map((option) => {
              const isSel = selected === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onSelect(option.value);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-4 py-3 hover:bg-base-200 text-sm font-grotesk flex items-center justify-between gap-2 ${
                    isSel ? "bg-base-200 font-medium" : ""
                  }`}
                >
                  <span>{option.label}</span>
                  {isSel && (
                    <HiCheck className="w-4 h-4 text-neutral flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Single-select training-type field. Collapsed by default showing the selected
// type; the searchable list of options is revealed when the field is tapped and
// closes after a pick (only one training type per log).
export const TrainingTypePicker: React.FC<{
  options: { value: string; label: string }[];
  selected: { value: string; label: string } | null;
  onSelect: (value: string | null) => void;
  placeholder?: string;
  emptyAction?: { label: string; onClick: () => void };
}> = ({ options, selected, onSelect, placeholder, emptyAction }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const showEmptyAction = !selected && options.length === 0 && !!emptyAction;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase()),
  );

  const toggleOpen = () => {
    if (showEmptyAction) return;
    setOpen((o) => {
      const next = !o;
      if (next) setTimeout(() => inputRef.current?.focus(), 0);
      else setSearch("");
      return next;
    });
  };

  return (
    <div className="relative" ref={ref}>
      <div
        className={`relative border border-base-300 rounded-md bg-base-200 ${
          showEmptyAction ? "" : "cursor-pointer"
        }`}
        onClick={toggleOpen}
      >
        <label className="absolute left-4 top-1 text-xs text-base-content/60 font-grotesk pointer-events-none z-10">
          Training type
        </label>
        <div
          className={`flex items-center gap-2 pl-4 pt-6 pb-2 min-h-[3.5rem] ${
            showEmptyAction ? "pr-4" : "pr-11"
          }`}
        >
          {selected ? (
            <span className="inline-flex items-center p-2 rounded-md text-sm font-geist bg-neutral text-white font-medium">
              {selected.label}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(null);
                }}
                className="ml-1.5 hover:text-white/60"
              >
                <HiX className="w-3 h-3" />
              </button>
            </span>
          ) : (
            <>
              <span className="flex-1 text-sm text-base-content/40">
                {placeholder}
              </span>
              {showEmptyAction && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    emptyAction.onClick();
                  }}
                  className="shrink-0 px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-white hover:opacity-90 transition-opacity"
                >
                  {emptyAction.label}
                </button>
              )}
            </>
          )}
        </div>
        {!showEmptyAction && (
          <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
            <HiChevronDown
              className={`w-5 h-5 text-base-content/50 transition-transform ${
                open ? "rotate-180" : ""
              }`}
            />
          </div>
        )}
      </div>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-md shadow-lg overflow-hidden">
          <div className="p-2 border-b border-base-200">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search training types..."
              className="w-full px-3 py-2 rounded bg-base-200 text-sm focus:outline-none focus:ring-0"
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length > 0 ? (
              filtered.map((option) => {
                const isSel = selected?.value === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onSelect(isSel ? null : option.value);
                      setOpen(false);
                      setSearch("");
                    }}
                    className={`w-full text-left px-4 py-3 hover:bg-base-200 text-sm font-grotesk flex items-center justify-between gap-2 ${
                      isSel ? "bg-base-200 font-medium" : ""
                    }`}
                  >
                    <span>{option.label}</span>
                    {isSel && (
                      <HiCheck className="w-4 h-4 text-neutral flex-shrink-0" />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-3 text-sm text-base-content/50 font-grotesk">
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Date helpers: dateID is YYYYMMDD; <input type="date"> wants YYYY-MM-DD.
const dateIDToInputValue = (id: string): string =>
  id && id.length >= 8
    ? `${id.substring(0, 4)}-${id.substring(4, 6)}-${id.substring(6, 8)}`
    : "";
const inputValueToDateID = (val: string): string => val.replace(/-/g, "");
const formatDateID = (id: string): string => {
  const iso = dateIDToInputValue(id);
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const TrainingLogForm: React.FC<TrainingLogFormProps> = ({
  open,
  onClose,
  context,
  onSaved,
  editMode = false,
  logId,
  initialData,
}) => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();

  // Date the log is being recorded against. Seeded from context but editable
  // in the form so the athlete can log for a different day.
  const [dateID, setDateID] = useState(context.dateID);
  const dateInputRef = useRef<HTMLInputElement>(null);

  // ── Core required fields ───────────────────────────────────────────────
  const [muscular, setMuscular] = useState(1);
  const [respiratory, setRespiratory] = useState(1);
  const [duration, setDuration] = useState("");
  const [durationError, setDurationError] = useState("");
  const [selectedTrainingType, setSelectedTrainingType] =
    useState<TrainingType | null>(null);

  // ── Optional core fields ───────────────────────────────────────────────
  const [notes, setNotes] = useState("");
  const [trainingPerformed, setTrainingPerformed] = useState(1);

  // ── Training types catalogue ───────────────────────────────────────────
  const [availableTrainingTypes, setAvailableTrainingTypes] = useState<
    TrainingType[]
  >([]);
  const [availableSports, setAvailableSports] = useState<Sport[]>([]);
  const [sportFilter, setSportFilter] = useState<string>("all");

  // ── Linked logs ────────────────────────────────────────────────────────
  // Only one linked-log editor is expanded at a time. Each chip reflects
  // whether that log currently has data ("linked") and tapping it opens the
  // editor to link / edit it.
  type LinkedLog = "session" | "niggle" | "injury" | "performance" | "activity";
  const [openLinked, setOpenLinked] = useState<LinkedLog | null>(null);

  const [pendingSessionMeasurements, setPendingSessionMeasurements] = useState<
    ActivityMeasurement[] | null
  >(context.sessionMeasurements ?? null);
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);

  // Existing same-day logs the athlete can link instead of creating new ones.
  const [existingSessions, setExistingSessions] = useState<
    (Session & { id: string })[]
  >([]);
  const [, setExistingNiggles] = useState<
    (NiggleLog & { id: string })[]
  >([]);
  const [existingPerformances, setExistingPerformances] = useState<
    (PerformanceLog & { id: string })[]
  >([]);
  const [existingActivities, setExistingActivities] = useState<
    (ActivityLog & { id: string })[]
  >([]);
  const [selectedExistingActivityId, setSelectedExistingActivityId] = useState<
    string | null
  >(null);
  // Sessions linked to this log (plan 3a, multi-select). `primarySessionId`
  // is the one whose training type the log takes; it becomes
  // `linkedSessionIDs[0]` on save. With one session it is that session; with
  // several the athlete must pick (decision 6).
  const [selectedSessionIds, setSelectedSessionIds] = useState<string[]>([]);
  const [primarySessionId, setPrimarySessionId] = useState<string | null>(
    null,
  );
  // Ids linked when the form opened (edit mode) so deselected ones can be
  // unlinked on save.
  const initialSessionIdsRef = useRef<string[]>([]);
  const [selectedExistingNiggleId, setSelectedExistingNiggleId] = useState<
    string | null
  >(null);
  const [selectedExistingPerformanceId, setSelectedExistingPerformanceId] =
    useState<string | null>(null);

  const [nigglePain, setNigglePain] = useState(1);
  const [niggleImpact, setNiggleImpact] = useState(1);
  const [niggleNotes, setNiggleNotes] = useState("");

  const [pain, setPain] = useState(false);
  const [injured, setInjured] = useState(false);
  // Pain / injury section; opens on its own when the log already has either.
  const [showPainSection, setShowPainSection] = useState(false);
  const [addNiggle, setAddNiggle] = useState(false);

  // Captured from the reused AddPerformanceLogForm — persisted and linked to
  // the training log on save. Holds the exact same inputs as the real
  // performance log.
  const [pendingPerformance, setPendingPerformance] =
    useState<PendingPerformance | null>(null);
  const [isPerformanceModalOpen, setIsPerformanceModalOpen] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { getActivityName } = useActivityNameResolver(
    pendingSessionMeasurements ?? undefined,
    currentUser?.uid,
  );

  // Load sports + training types when the form opens. Reads trainingSportIDs
  // from the public user doc (UserPublicData) so we match Athlete Preferences
  // even if auth context is briefly stale.
  useEffect(() => {
    if (!open || !currentUser?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const publicData = await getUserPublicData(currentUser.uid);
        const sportIDs =
          publicData?.trainingSportIDs ?? userData?.trainingSportIDs ?? [];
        const customs: CustomTrainingType[] | undefined =
          publicData?.customTrainingTypes ?? userData?.customTrainingTypes;
        const [types, sports] = await Promise.all([
          fetchTrainingTypesForUser(sportIDs, customs),
          trainingTypeService.fetchSports(),
        ]);
        if (cancelled) return;
        setAvailableTrainingTypes(types);
        const userSports = sports
          .filter((s) => !!s.id && sportIDs.includes(s.id))
          .sort((a, b) => a.name.localeCompare(b.name));
        setAvailableSports(userSports);
        setSportFilter((prev) => {
          if (prev === "all") return prev;
          if (userSports.some((s) => s.id === prev)) return prev;
          return "all";
        });
      } catch {
        if (cancelled) return;
        setAvailableTrainingTypes([]);
        setAvailableSports([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    currentUser?.uid,
    (userData?.trainingSportIDs ?? []).join(","),
    userData?.customTrainingTypes,
  ]);

  // Populate (edit) or reset (create) the form when it transitions to open.
  const prevOpenRef = useRef(false);
  const editSeededRef = useRef<string | null>(null);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Seed the working date once per open. When editing, the log's own day is
      // authoritative — the opener may be viewing a different date (e.g. today)
      // than the log being edited. Otherwise use the contextual date.
      setDateID(
        editMode && initialData?.dateID ? initialData.dateID : context.dateID,
      );

      // Apply any supplied initial data (edit, or a contextual create such as
      // finishing a session / linking a wearable activity where duration and
      // notes are pre-filled). Otherwise start from a clean form.
      if (initialData) {
        setMuscular(initialData.muscular ?? 1);
        setRespiratory(initialData.respiratory ?? 1);
        setDuration(
          initialData.duration != null ? String(initialData.duration) : "",
        );
        setDurationError("");
        setNotes(initialData.notes ?? "");
        setTrainingPerformed(initialData.trainingPerformed ?? 1);
        setPain(initialData.pain ?? false);
        setInjured(initialData.injured ?? false);
        setShowPainSection(!!(initialData.pain || initialData.injured));
        const linkedIds = getLinkedSessionIDs(initialData);
        if (context.sessionId && !linkedIds.includes(context.sessionId)) {
          linkedIds.push(context.sessionId);
        }
        setSelectedSessionIds(linkedIds);
        setPrimarySessionId(
          getPrimarySessionID(initialData) ??
            (linkedIds.length === 1 ? linkedIds[0] : null),
        );
        initialSessionIdsRef.current = editMode
          ? getLinkedSessionIDs(initialData)
          : [];
        setSelectedExistingActivityId(initialData.linkedActivityLogID ?? null);
        // Force the training type to reseed once the catalogue loads.
        editSeededRef.current = null;
      } else {
        resetState();
        if (context.sessionId) {
          setSelectedSessionIds([context.sessionId]);
          setPrimarySessionId(context.sessionId);
        }
        initialSessionIdsRef.current = [];
      }

      // When the form is opened for a specific session (completing a session or
      // logging an unlinked one from the dashboard), expand the session details
      // panel so the coach's notes and activities are visible immediately.
      if (context.sessionId) {
        setOpenLinked("session");
      }
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editMode, initialData, context.dateID]);

  // Seed the selected training type once the catalogue is available: prefer
  // the stored trainingTypeID, then fall back to legacy tags matched against
  // names / alternativeNames.
  useEffect(() => {
    if (!open || !initialData) return;
    if (!initialData.trainingTypeID && !initialData.tag?.length) return;
    if (availableTrainingTypes.length === 0) return;
    const seedKey = logId ?? "seed";
    if (editSeededRef.current === seedKey) return;
    const matched = resolveTrainingTypeForLog(
      availableTrainingTypes,
      initialData.trainingTypeID,
      initialData.tag,
    );
    setSelectedTrainingType(matched);
    editSeededRef.current = seedKey;
  }, [open, initialData, availableTrainingTypes, logId]);

  // Fetch the day's existing logs so the athlete can link them instead of
  // creating new ones. Reruns whenever the working date changes.
  useEffect(() => {
    if (!open || !currentUser?.uid) return;
    let cancelled = false;
    const uid = currentUser.uid;
    (async () => {
      try {
        const refs = await getDayPostReferences(uid, dateID);
        if (cancelled) return;
        const [sessions, niggles, refPerformances, performedOnDay, activities] =
          await Promise.all([
            getSessions(uid, refs?.sessionIDs ?? []),
            getNiggleLogs(uid, refs?.niggleLogIDs ?? []),
            getPerformanceLogs(uid, refs?.performanceLogIDs ?? []),
            // Performances performed on this day but logged (and therefore
            // referenced) under a different day post — mirrors how the diary
            // redistributes performances by performanceDate.
            getPerformanceLogsForDateRange(uid, dateID, dateID),
            getActivityLogs(uid, refs?.activityLogIDs ?? []),
          ]);
        if (cancelled) return;
        // Merge both sources and de-duplicate by id.
        const performanceById = new Map<
          string,
          PerformanceLog & { id: string }
        >();
        [...refPerformances, ...performedOnDay].forEach((p) => {
          performanceById.set(p.id, p);
        });
        const performances = Array.from(performanceById.values());
        setExistingSessions(sessions);
        setExistingNiggles(niggles);
        setExistingPerformances(performances);
        // Skip activities the athlete chose to ignore, but always keep the one
        // that opened the form (if any).
        setExistingActivities(
          activities.filter(
            (a) => !a.ignore || a.id === context.activityLogId,
          ),
        );
        // When editing, surface the niggle / performance already linked to this
        // training log so they show as linked chips.
        if (editMode && logId) {
          const linkedNiggle = niggles.find(
            (n) => n.linkedTrainingLogID === logId,
          );
          if (linkedNiggle) setSelectedExistingNiggleId(linkedNiggle.id);
          const linkedPerformance = performances.find(
            (p) => p.linkedTrainingLogID === logId,
          );
          if (linkedPerformance)
            setSelectedExistingPerformanceId(linkedPerformance.id);
        }
      } catch (err) {
        if (!cancelled) {
          setExistingSessions([]);
          setExistingNiggles([]);
          setExistingPerformances([]);
          setExistingActivities([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, currentUser?.uid, dateID, context.activityLogId, editMode, logId]);

  // When the form opens for a session from the Dashboard ("Create Log" /
  // sheet) the session docs arrive after the open effect: seed the duration
  // (sum) and type from them once, only for new logs with an empty duration.
  useEffect(() => {
    if (!open || editMode || existingSessions.length === 0) return;
    if (selectedSessionIds.length === 0 || duration.trim() !== "") return;
    const sessions = selectedSessionIds
      .map((id) => existingSessions.find((s) => s.id === id))
      .filter(Boolean) as Session[];
    const total = sumSessionDurationMinutes(sessions);
    if (total > 0) setDuration(String(total));
    if (sessions.length === 1 && sessions[0].trainingTypeID) {
      const match = availableTrainingTypes.find(
        (t) => t.id === sessions[0].trainingTypeID,
      );
      if (match) setSelectedTrainingType((prev) => prev ?? match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editMode, existingSessions, availableTrainingTypes]);

  // ── Derived values (identical formulas to the original form) ────────────
  const rpe = calcRpe(muscular, respiratory);
  const durationInMinutes = parseDurationToMinutes(duration);
  const loadModifier = calcLoadModifier(selectedTrainingType);
  const load = calcLoad(rpe, durationInMinutes, loadModifier);
  // Performance rating only surfaces for harder sessions. RPE moves in 0.5
  // steps, so "> 7" is the same set of values as the ">= 7.5" persistence rule.
  const showPerformanceRating = rpe > 7;

  // Whether each linked log currently holds data ("linked" / activated chip).
  const sessionLinked = !!(
    pendingSessionMeasurements?.length || selectedSessionIds.length > 0
  );

  // Full session documents backing the current links, in selection order.
  // Used for the chips, coach notes, duration sum and the primary-type picker.
  const selectedSessions = selectedSessionIds
    .map((id) => existingSessions.find((s) => s.id === id))
    .filter((s): s is Session & { id: string } => !!s);
  const needsPrimary = requiresPrimarySelection(selectedSessionIds);
  const primaryMissing =
    needsPrimary &&
    (!primarySessionId || !selectedSessionIds.includes(primarySessionId));
  const resolveSessionTypeName = (
    session: Pick<Session, "trainingTypeID" | "sessionTypeID">,
  ): string | null =>
    trainingTypeService.getSessionTrainingTypeName(
      session,
      availableTrainingTypes,
    ) || null;
  const primaryTypeOptions: PrimaryTypeOption[] = needsPrimary
    ? getPrimaryTypeOptions(selectedSessions, resolveSessionTypeName)
    : [];
  const sessionLabel = (session: Session & { id: string }): string =>
    session.name?.trim() ||
    resolveSessionTypeName(session) ||
    `Session · ${session.ActivityMeasurementList?.length ?? 0} activities`;

  // Re-seed the duration from the linked sessions whenever the selection
  // changes: the sum of each session's start–end (decision 6), still editable.
  const seedDurationFromSessions = (ids: string[]) => {
    const sessions = ids
      .map((id) => existingSessions.find((s) => s.id === id))
      .filter(Boolean) as Session[];
    const total = sumSessionDurationMinutes(sessions);
    if (total > 0) {
      setDuration(String(total));
      setDurationError("");
    }
  };

  // Seed the training type from the primary session when it has one in the
  // athlete's catalogue.
  const seedTypeFromSession = (sessionId: string | null) => {
    if (!sessionId) return;
    const session = existingSessions.find((s) => s.id === sessionId);
    const typeId = session?.trainingTypeID;
    if (!typeId) return;
    const match = availableTrainingTypes.find((t) => t.id === typeId);
    if (match) setSelectedTrainingType(match);
  };

  const toggleSession = (id: string) => {
    const wasSelected = selectedSessionIds.includes(id);
    const next = wasSelected
      ? selectedSessionIds.filter((s) => s !== id)
      : [...selectedSessionIds, id];
    setSelectedSessionIds(next);
    if (next.length > 0) setPendingSessionMeasurements(null);
    // Keep the primary valid: a single remaining session is its own primary;
    // with several, drop a removed primary and make the athlete choose.
    if (next.length === 1) {
      setPrimarySessionId(next[0]);
      seedTypeFromSession(next[0]);
    } else if (primarySessionId && !next.includes(primarySessionId)) {
      setPrimarySessionId(null);
    }
    seedDurationFromSessions(next);
  };

  const choosePrimary = (sessionId: string) => {
    setPrimarySessionId(sessionId);
    seedTypeFromSession(sessionId);
  };

  const linkedSessionBlocks = selectedSessions.flatMap(
    (s) => s.activityBlocks ?? [],
  );
  const performanceLinked =
    !!pendingPerformance || !!selectedExistingPerformanceId;
  const activityLinked = !!context.activityLogId || !!selectedExistingActivityId;
  // The wearable activity currently in focus: the one that opened the form, or
  // the one the athlete picked from the day's available activities.
  const activeActivity =
    existingActivities.find(
      (a) => a.id === (context.activityLogId || selectedExistingActivityId),
    ) ?? null;

  const validateDuration = (value: string): boolean => {
    if (!value.trim()) {
      setDurationError("Duration is required");
      return false;
    }
    if (parseDurationToMinutes(value) <= 0) {
      setDurationError("Enter a valid duration (e.g. 60 or 1:30)");
      return false;
    }
    setDurationError("");
    return true;
  };

  const canSubmit = durationInMinutes > 0 && !isSubmitting && !primaryMissing;

  const resetState = () => {
    setMuscular(1);
    setRespiratory(1);
    setDuration("");
    setDurationError("");
    setSelectedTrainingType(null);
    setNotes("");
    setTrainingPerformed(1);
    setOpenLinked(null);
    setNigglePain(1);
    setNiggleImpact(1);
    setNiggleNotes("");
    setPain(false);
    setInjured(false);
    setShowPainSection(false);
    setAddNiggle(false);
    setPendingPerformance(null);
    setIsPerformanceModalOpen(false);
    setSelectedSessionIds([]);
    setPrimarySessionId(null);
    setSelectedExistingNiggleId(null);
    setSelectedExistingPerformanceId(null);
    setSelectedExistingActivityId(null);
    setSubmitError(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSubmit = async () => {
    if (!currentUser?.uid) {
      setSubmitError("Please log in and try again.");
      return;
    }
    if (!validateDuration(duration)) return;

    setIsSubmitting(true);
    setSubmitError(null);
    const uid = currentUser.uid;

    try {
      const trainingLogData: any = {
        load,
        muscular,
        respiratory,
        rpe,
        notes: notes.trim(),
        // Keep tag populated with the training type name so historical
        // display, feed search and reports continue to work.
        tag: selectedTrainingType ? [selectedTrainingType.name] : [],
        duration: durationInMinutes,
        pain,
        injured,
      };
      if (selectedTrainingType) {
        trainingLogData.trainingTypeID = selectedTrainingType.id;
      } else if (editMode) {
        // Clear a previously saved type when the athlete removes it.
        const { deleteField } = await import("firebase/firestore");
        trainingLogData.trainingTypeID = deleteField();
      }
      // Only persist trainingPerformed when effective RPE >= 7.5 (unchanged).
      if (rpe >= 7.5) {
        trainingLogData.trainingPerformed = trainingPerformed;
      }

      let trainingLogId: string;
      if (editMode && logId) {
        await updateTrainingLog(uid, logId, trainingLogData);
        trainingLogId = logId;
      } else {
        trainingLogId = await addTrainingLog(uid, trainingLogData, dateID);
        await addLogIDToDayPost(uid, dateID, trainingLogId, "trainingLogIDs");
      }

      // ── Session links (plan 3a) ─────────────────────────────────────
      // The selected sessions (existing ones the athlete picked and/or the
      // one passed in via context) are linked with the primary first; when
      // none is selected a new session is built from in-form measurements.
      const orderedSessionIds =
        orderLinkedSessionIDs(selectedSessionIds, primarySessionId) ??
        selectedSessionIds;
      let sessionIdsToLink = [...orderedSessionIds];
      if (
        sessionIdsToLink.length === 0 &&
        pendingSessionMeasurements &&
        pendingSessionMeasurements.length > 0
      ) {
        try {
          const sessionName = selectedTrainingType?.name;
          const trainingTypeID = selectedTrainingType?.id;
          const createdId = await sessionService.createSession(uid, {
            ActivityMeasurementList: pendingSessionMeasurements,
            plannedDate: convertDateIDToYYYYMMDD(dateID),
            description: "",
            status: "complete" as const,
            linkedTrainingLogID: trainingLogId,
            ...(sessionName ? { name: sessionName } : {}),
            ...(trainingTypeID ? { trainingTypeID } : {}),
          });
          sessionIdsToLink = [createdId];
        } catch (err) {
          console.error("Error creating session for training log:", err);
        }
      }
      // Measurements edited while completing a session (Session.tsx) belong
      // to the session that opened the form.
      if (
        context.sessionId &&
        pendingSessionMeasurements &&
        sessionIdsToLink.includes(context.sessionId)
      ) {
        try {
          await sessionService.updateSession(uid, context.sessionId, {
            ActivityMeasurementList: pendingSessionMeasurements,
          });
        } catch (err) {
          console.error("Error saving session measurements:", err);
        }
      }
      for (const sessionId of sessionIdsToLink) {
        try {
          await sessionService.linkSessionToLog(
            uid,
            sessionId,
            trainingLogId,
            dateID,
          );
        } catch (err) {
          console.error("Error linking session to training log:", err);
        }
      }
      // Sessions deselected while editing are unlinked (they reappear under
      // "Sessions to log").
      for (const sessionId of initialSessionIdsRef.current) {
        if (sessionIdsToLink.includes(sessionId)) continue;
        try {
          await sessionService.unlinkSessionFromLog(
            uid,
            sessionId,
            trainingLogId,
          );
        } catch (err) {
          console.error("Error unlinking session from training log:", err);
        }
      }
      // linkSessionToLog appends (arrayUnion), so write the final order once
      // when several sessions are linked so the chosen primary is element 0.
      if (sessionIdsToLink.length > 1) {
        try {
          await updateTrainingLog(uid, trainingLogId, {
            linkedSessionIDs: sessionIdsToLink,
            linkedSessionID: sessionIdsToLink[0],
          });
        } catch (err) {
          console.error("Error ordering linked sessions:", err);
        }
      }

      // ── Activity link ───────────────────────────────────────────────
      // The activity that opened the form takes precedence; otherwise link the
      // one the athlete picked from the day's available wearable activities.
      const activityIdToLink =
        context.activityLogId || selectedExistingActivityId;
      if (activityIdToLink) {
        try {
          await linkActivityToTraining(uid, activityIdToLink, trainingLogId);
        } catch (err) {
          console.error("Error linking activity to training log:", err);
        }
      }

      // ── Niggle link ─────────────────────────────────────────────────
      if (selectedExistingNiggleId) {
        // Link an existing niggle to this training log.
        try {
          await updateNiggleLog(uid, selectedExistingNiggleId, {
            linkedTrainingLogID: trainingLogId,
          });
        } catch (err) {
          console.error("Error linking existing niggle log:", err);
        }
      } else if (!editMode && pain && addNiggle) {
        // "Add a niggle" is only offered on new logs; on edit the niggle
        // already exists and is edited from the diary.
        try {
          const niggleId = await addNiggleLog(
            uid,
            {
              pain: nigglePain,
              impact: niggleImpact,
              notes: niggleNotes.trim(),
              linkedTrainingLogID: trainingLogId,
            },
            dateID,
          );
          await addLogIDToDayPost(uid, dateID, niggleId, "niggleLogIDs");
        } catch (err) {
          console.error("Error creating linked niggle log:", err);
        }
      }

      // ── Performance link ────────────────────────────────────────────
      if (selectedExistingPerformanceId) {
        // Link an existing performance to this training log.
        try {
          await updatePerformanceLog(uid, selectedExistingPerformanceId, {
            linkedTrainingLogID: trainingLogId,
          });
        } catch (err) {
          console.error("Error linking existing performance log:", err);
        }
      } else if (pendingPerformance) {
        try {
          const perfId = await addPerformanceLog(
            uid,
            {
              notes: pendingPerformance.notes,
              performanceExpectation: pendingPerformance.performanceExpectation,
              performanceResult: pendingPerformance.performanceResult,
              performanceTitle: pendingPerformance.performanceTitle,
              performanceDate: pendingPerformance.performanceDate,
              linkedTrainingLogID: trainingLogId,
              ...(pendingPerformance.activity
                ? { activity: pendingPerformance.activity }
                : {}),
              ...(pendingPerformance.tag
                ? { tag: pendingPerformance.tag }
                : {}),
            },
            dateID,
          );
          await addLogIDToDayPost(uid, dateID, perfId, "performanceLogIDs");
          // Defensive: ensure the link is set even if create payload changes.
          await updatePerformanceLog(uid, perfId, {
            linkedTrainingLogID: trainingLogId,
          });
        } catch (err) {
          console.error("Error creating linked performance log:", err);
        }
      }

      onSaved?.(trainingLogId);
      handleClose();
    } catch (error) {
      console.error("Error adding training log:", error);
      setSubmitError("Could not save the training log. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const sportFilterOptions = [
    { value: "all", label: "All sports" },
    ...availableSports
      .filter((s) => !!s.id)
      .map((s) => ({ value: s.id as string, label: s.name }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
      ),
  ];

  const filteredTrainingTypes = availableTrainingTypes.filter((t) => {
    if (!t.id) return false;
    if (sportFilter === "all") return true;
    const sport = availableSports.find((s) => s.id === sportFilter);
    if (!sport) return true;
    if (t.isCustomTrainingType) {
      return (
        !t.relatedTrainingTypeID ||
        sport.trainingTypeIDs?.includes(t.relatedTrainingTypeID)
      );
    }
    return sport.trainingTypeIDs?.includes(t.id);
  });

  const trainingTypeOptions = filteredTrainingTypes
    .map((t) => ({
      value: t.id as string,
      label: t.name,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );

  if (!open) return null;

  return (
    <>
      {/* Plain fixed overlay (no Headless UI focus trap) so nested session /
          performance modals remain fully interactive. */}
      <div className="fixed inset-0 z-[10000] bg-black/40 backdrop-blur-sm overflow-y-auto">
        <div className="flex min-h-screen md:items-center md:justify-center items-start justify-center">
          <div>
            <div className="w-full max-w-2xl mx-auto overflow-hidden rounded-none md:rounded-md bg-base-100 text-left align-middle shadow-xl min-h-screen md:min-h-0 md:max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-start p-6 flex-shrink-0">
                  <div>
                    <h2 className="text-lg font-grotesk">
                      {editMode ? "Edit Training" : "Log Training"}
                    </h2>
                    {/* Clickable date — opens a native date picker */}
                    <div className="relative mt-1">
                      <button
                        type="button"
                        onClick={() => {
                          const el = dateInputRef.current;
                          if (!el) return;
                          if (typeof (el as any).showPicker === "function") {
                            (el as any).showPicker();
                          } else {
                            el.focus();
                            el.click();
                          }
                        }}
                        className="flex items-center gap-1.5 text-sm text-base-content/60 hover:text-base-content transition-colors"
                      >
                        <span className="font-medium underline decoration-dotted underline-offset-4">
                          {formatDateID(dateID)}
                        </span>
                      </button>
                      <input
                        ref={dateInputRef}
                        type="date"
                        value={dateIDToInputValue(dateID)}
                        onChange={(e) => {
                          const next = inputValueToDateID(e.target.value);
                          if (next) setDateID(next);
                        }}
                        className="absolute left-0 top-6 w-0 h-0 opacity-0 pointer-events-none"
                        tabIndex={-1}
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className="flex items-center gap-1.5"
                      aria-label="Step 1 of 1"
                    >
                      <span className="h-1.5 w-6 rounded-full bg-neutral" />
                    </div>
                    <button
                      onClick={handleClose}
                      className="bg-base-200 p-2 rounded-full"
                      aria-label="Close"
                    >
                      <HiX className="w-5 h-5 text-base-content/80" />
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-7">
                  {/* Sport filter + training type (optional) — at the top */}
                  {availableSports.length > 1 && (
                    <SportFilterPicker
                      options={sportFilterOptions}
                      selected={sportFilter}
                      onSelect={(value) => {
                        setSportFilter(value);
                        if (!selectedTrainingType?.id || value === "all") {
                          return;
                        }
                        const sport = availableSports.find(
                          (s) => s.id === value,
                        );
                        if (!sport) return;
                        const typeID = selectedTrainingType.id;
                        const inSport = selectedTrainingType.isCustomTrainingType
                          ? !selectedTrainingType.relatedTrainingTypeID ||
                            sport.trainingTypeIDs?.includes(
                              selectedTrainingType.relatedTrainingTypeID,
                            )
                          : sport.trainingTypeIDs?.includes(typeID);
                        if (!inSport) setSelectedTrainingType(null);
                      }}
                    />
                  )}
                  <TrainingTypePicker
                    options={trainingTypeOptions}
                    selected={
                      selectedTrainingType && selectedTrainingType.id
                        ? {
                            value: selectedTrainingType.id,
                            label: selectedTrainingType.name,
                          }
                        : null
                    }
                    onSelect={(value) => {
                      if (!value) {
                        setSelectedTrainingType(null);
                        return;
                      }
                      const t = availableTrainingTypes.find(
                        (tt) => tt.id === value,
                      );
                      setSelectedTrainingType(t ?? null);
                    }}
                    placeholder={
                      filteredTrainingTypes.length
                        ? "Select training type"
                        : "No training types available"
                    }
                    emptyAction={
                      filteredTrainingTypes.length === 0
                        ? {
                            label: "Add Sport",
                            onClick: () => {
                              handleClose();
                              navigate("/settings/athlete-preferences");
                            },
                          }
                        : undefined
                    }
                  />

                  {/* Duration (required) */}
                  <div>
                    <FloatingField label="Duration" required>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={duration}
                        onChange={(e) => {
                          setDuration(e.target.value);
                          if (durationError) validateDuration(e.target.value);
                        }}
                        onBlur={(e) => validateDuration(e.target.value)}
                        placeholder="60 or 1:30"
                        className="w-full px-4 pt-7 pr-14 font-semibold pb-2.5 rounded-xl bg-base-200 border border-base-300 focus:border-base-300 focus:outline-none focus:ring-0 text-[16px] font-geist tabular-nums placeholder:font-sans placeholder:text-base-content/40"
                      />
                      <span className="absolute right-4 bottom-3 text-sm font-light text-base-content/40 pointer-events-none">
                        min
                      </span>
                    </FloatingField>
                    {/* Quick-select minutes — only shown until a duration is
                        entered or selected. */}
                    {duration.trim() === "" && (
                      <DurationQuickChips
                        onSelect={(mins) => {
                          setDuration(String(mins));
                          validateDuration(String(mins));
                        }}
                      />
                    )}
                    {durationError && (
                      <p className="text-xs text-error mt-1.5 font-grotesk">
                        {durationError}
                      </p>
                    )}
                  </div>

                  {/* Effort sliders */}
                  <div className="space-y-6">
                    <Slider
                      label="Muscular Effort"
                      question="How much muscular effort did you exert?"
                      value={muscular}
                      onChange={setMuscular}
                      description={MUSCULAR_DESCRIPTIONS[muscular]}
                    />
                    <Slider
                      label="Respiratory Effort"
                      question="How breathless did the session make you?"
                      value={respiratory}
                      onChange={setRespiratory}
                      description={RESPIRATION_DESCRIPTIONS[respiratory]}
                    />
                  </div>

                  <PainInjurySection
                    className="-mt-2"
                    open={showPainSection}
                    onOpenChange={setShowPainSection}
                    pain={pain}
                    onPainChange={setPain}
                    injured={injured}
                    onInjuredChange={setInjured}
                    allowNiggle={!editMode}
                    addNiggle={addNiggle}
                    onAddNiggleChange={setAddNiggle}
                    nigglePain={nigglePain}
                    onNigglePainChange={setNigglePain}
                    niggleImpact={niggleImpact}
                    onNiggleImpactChange={setNiggleImpact}
                    niggleNotes={niggleNotes}
                    onNiggleNotesChange={setNiggleNotes}
                    niggleSubtitle="Logged as a Niggle linked to this session"
                  />

                  {/* Performance rating — only when total RPE > 7 */}
                  {showPerformanceRating && (
                    <Slider
                      label="Performance rating"
                      question="How much muscular effort did you exert?"
                      value={trainingPerformed}
                      onChange={setTrainingPerformed}
                      description={`How well you performed this session: ${trainingPerformed}/10`}
                      min={1}
                      max={10}
                    />
                  )}

                  {/* Notes (optional) */}
                  <FloatingField label="Notes">
                    <textarea
                      className="px-4 pt-6 pb-2.5 rounded-md bg-base-200 border border-transparent focus:border-base-300 h-28 resize-none focus:outline-none focus:ring-0 text-[16px] w-full placeholder:text-base-content/40"
                      placeholder="Anything worth remembering about this session..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </FloatingField>

                  {/* ── Linked logs (activated chips) ─────────────────── */}
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {(
                        [
                          { key: "session", label: "Session", active: sessionLinked },
                          {
                            key: "performance",
                            label: "Performance",
                            active: performanceLinked,
                          },
                          {
                            key: "activity" as LinkedLog,
                            label: "Activity",
                            active: activityLinked,
                          },
                        ] as { key: LinkedLog; label: string; active: boolean }[]
                      )
                        // Linked chips first, preserving the original order within
                        // each group (stable sort).
                        .sort((a, b) => Number(b.active) - Number(a.active))
                        .map((chip) => (
                          <LinkedChip
                            key={chip.key}
                            label={chip.label}
                            active={chip.active}
                            open={openLinked === chip.key}
                            onClick={() =>
                              setOpenLinked((o) =>
                                o === chip.key ? null : chip.key,
                              )
                            }
                          />
                        ))}
                    </div>

                    {/* Session editor (multi-select, plan 3a) */}
                    {openLinked === "session" && (
                      <EditorPanel>
                        <LinkedSessionChips
                          sessions={selectedSessions.map((s) => ({
                            id: s.id,
                            label: sessionLabel(s),
                          }))}
                          primaryId={primarySessionId}
                          onRemove={toggleSession}
                        />
                        {needsPrimary && (
                          <PrimaryTypePicker
                            options={primaryTypeOptions}
                            selectedSessionID={primarySessionId}
                            onSelect={choosePrimary}
                          />
                        )}
                        {/* Coach-authored Session Note sections for the linked
                            sessions, shown read-only above the activities. */}
                        {linkedSessionBlocks.length > 0 && (
                          <SessionNotes blocks={linkedSessionBlocks} />
                        )}
                        <ExistingLogPicker
                          multiple
                          title={
                            selectedSessionIds.length > 0
                              ? "Add another session"
                              : "Link existing sessions"
                          }
                          items={existingSessions.map((s) => ({
                            id: s.id,
                            label: sessionLabel(s),
                            sublabel: `${
                              s.ActivityMeasurementList?.length ?? 0
                            } activities${
                              sumSessionDurationMinutes([s]) > 0
                                ? ` · ${sumSessionDurationMinutes([s])} min`
                                : ""
                            }${
                              s.linkedTrainingLogID &&
                              s.linkedTrainingLogID !== logId
                                ? " · linked to another log"
                                : ""
                            }`,
                          }))}
                          selectedIds={selectedSessionIds}
                          onToggle={toggleSession}
                        />
                        {selectedSessionIds.length === 0 && (
                          <>
                            {pendingSessionMeasurements &&
                            pendingSessionMeasurements.length > 0 ? (
                              <SessionActivitiesTable
                                measurements={pendingSessionMeasurements}
                                getActivityName={getActivityName}
                              />
                            ) : (
                              <p className="text-xs text-base-content/60">
                                No session activities attached yet.
                              </p>
                            )}
                            <button
                              type="button"
                              onClick={() => setIsSessionModalOpen(true)}
                              className="w-full text-sm bg-base-100 hover:bg-base-200 border border-base-300 py-3 px-4 rounded-md font-medium flex items-center justify-between transition-colors"
                            >
                              <span>
                                {pendingSessionMeasurements?.length
                                  ? "Edit session details"
                                  : "Add new session details"}
                              </span>
                              <span className="text-base-content/50">+</span>
                            </button>
                          </>
                        )}
                      </EditorPanel>
                    )}

                    {/* Performance preview (full editor is the reused
                        AddPerformanceLogForm modal opened from the chip) */}
                    {/* Performance editor */}
                    {openLinked === "performance" && (
                      <EditorPanel>
                        <ExistingLogPicker
                          items={existingPerformances.map((p) => {
                            const { name, value } = summarizePerformance(p);
                            return {
                              id: p.id,
                              label: name,
                              sublabel: `${value ? `${value} · ` : ""}Rating ${
                                p.performanceExpectation
                              }/10${
                                p.tag?.length ? ` · ${p.tag.join(", ")}` : ""
                              }${
                                p.linkedTrainingLogID ? " · already linked" : ""
                              }`,
                            };
                          })}
                          selectedId={selectedExistingPerformanceId}
                          onSelect={(id) => {
                            setSelectedExistingPerformanceId(id);
                            if (id) setPendingPerformance(null);
                          }}
                        />

                        {!selectedExistingPerformanceId &&
                          (pendingPerformance ? (
                            <div className="flex items-start justify-between gap-3">
                              <div className="text-sm min-w-0">
                                <p className="font-medium truncate">
                                  {summarizePerformance(pendingPerformance).name}
                                </p>
                                {summarizePerformance(pendingPerformance)
                                  .value && (
                                  <p className="text-sm text-base-content/80 mt-0.5 font-geist tabular-nums truncate">
                                    {
                                      summarizePerformance(pendingPerformance)
                                        .value
                                    }
                                  </p>
                                )}
                                <p className="text-xs text-base-content/60 mt-0.5">
                                  Rating{" "}
                                  <span className="font-geist tabular-nums">
                                    {pendingPerformance.performanceExpectation}
                                    /10
                                  </span>
                                  {pendingPerformance.tag?.length
                                    ? ` · ${pendingPerformance.tag.join(", ")}`
                                    : ""}
                                </p>
                              </div>
                              <div className="flex gap-2 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => setIsPerformanceModalOpen(true)}
                                  className="text-xs font-medium text-base-content/70 hover:text-base-content"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setPendingPerformance(null)}
                                  className="text-xs font-medium text-error hover:text-error/80 font-grotesk min-h-9 px-1"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setIsPerformanceModalOpen(true)}
                              className="w-full text-sm bg-base-100 hover:bg-base-200 border border-base-300 py-3 px-4 rounded-md font-medium flex items-center justify-between transition-colors"
                            >
                              <span>Log new performance</span>
                              <span className="text-base-content/50">+</span>
                            </button>
                          ))}
                      </EditorPanel>
                    )}

                    {/* Activity editor — view wearable data + pick which one */}
                    {openLinked === "activity" && (
                      <EditorPanel>
                        {/* No wearable activity synced for the day. */}
                        {!context.activityLogId &&
                          existingActivities.length === 0 && (
                            <p className="text-sm text-base-content/60">
                              No wearable activity for today.
                            </p>
                          )}

                        {/* Picker only when the form wasn't opened from a
                            specific activity and there's more than one to pick. */}
                        {!context.activityLogId &&
                          existingActivities.length > 0 && (
                            <ExistingLogPicker
                              items={existingActivities.map((a) => {
                                const { name, metrics } = summarizeActivity(a);
                                return {
                                  id: a.id,
                                  label: name,
                                  sublabel: `${
                                    metrics.length
                                      ? metrics
                                          .map((m) => m.value)
                                          .join(" · ") + " · "
                                      : ""
                                  }${
                                    a.linkedTrainingLogID
                                      ? "already linked"
                                      : (a.provider ?? "wearable")
                                  }`,
                                };
                              })}
                              selectedId={selectedExistingActivityId}
                              onSelect={(id) =>
                                setSelectedExistingActivityId(id)
                              }
                            />
                          )}

                        {activeActivity ? (
                          <div className="space-y-3">
                            <div>
                              <p className="text-sm font-medium">
                                {summarizeActivity(activeActivity).name}
                              </p>
                              {activeActivity.provider && (
                                <p className="text-xs text-base-content/50 mt-0.5">
                                  {activeActivity.provider}
                                </p>
                              )}
                            </div>
                            {summarizeActivity(activeActivity).metrics.length >
                            0 ? (
                              <div className="grid grid-cols-2 gap-2">
                                {summarizeActivity(activeActivity).metrics.map(
                                  (m) => (
                                    <div
                                      key={m.label}
                                      className="rounded-md bg-base-100 border border-base-300 px-3 py-2"
                                    >
                                      <p className="text-[11px] uppercase tracking-wider text-base-content/50">
                                        {m.label}
                                      </p>
                                      <p className="text-sm font-geist tabular-nums font-medium mt-0.5">
                                        {m.value}
                                      </p>
                                    </div>
                                  ),
                                )}
                              </div>
                            ) : (
                              <p className="text-xs text-base-content/60">
                                No metrics available for this activity.
                              </p>
                            )}
                            {context.activityLogId && (
                              <p className="text-xs text-base-content/50">
                                This wearable activity will be linked to the
                                training log.
                              </p>
                            )}
                          </div>
                        ) : (
                          existingActivities.length > 0 && (
                            <p className="text-sm text-base-content/60">
                              Select a wearable activity to link and view its
                              data.
                            </p>
                          )
                        )}
                      </EditorPanel>
                    )}
                  </div>

                  {submitError && (
                    <p className="text-sm text-error font-grotesk">{submitError}</p>
                  )}
                </div>

                {/* Footer — floats over the scrolling content with a base→transparent
                    fade so it reads cleanly in light and dark mode. */}
                <div className="px-5 pb-5 pt-28 -mt-24 flex-shrink-0 relative z-50 flex items-center justify-between gap-3 bg-gradient-to-t from-base-100 from-30% to-transparent pointer-events-none">
                  <div className="flex items-center pointer-events-auto">
                    <button
                      type="button"
                      onClick={() => !isSubmitting && handleClose()}
                      disabled={isSubmitting}
                      className="ml-2 px-3 py-2 rounded-md text-sm font-medium text-base-content/45 hover:text-base-content/70 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    aria-label={editMode ? "Save changes" : "Log training"}
                    title={editMode ? "Save changes" : "Log training"}
                    className="pointer-events-auto w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center shadow-lg transition-opacity disabled:opacity-40"
                  >
                    {isSubmitting ? (
                      <span className="loading loading-spinner loading-sm" />
                    ) : (
                      <HiArrowRight className="w-6 h-6" />
                    )}
                  </button>
                </div>
            </div>
          </div>
        </div>
      </div>

      {/* Nested modals render as siblings of the plain overlay so back / escape
          only closes the top layer and their inputs stay interactive. */}
      {open && isSessionModalOpen && (
        <AddSessionDetailsModal
          isOpen={isSessionModalOpen}
          overlayZIndexClass="z-[10001]"
          syncHiddenColumns
          onClose={() => setIsSessionModalOpen(false)}
          trainingLogDate={convertDateIDToYYYYMMDD(dateID)}
          onSessionSaved={(_sessionId, measurements) => {
            setPendingSessionMeasurements(measurements);
            setOpenLinked("session");
            setIsSessionModalOpen(false);
          }}
        />
      )}

      {open && isPerformanceModalOpen && (
        <AddPerformanceLogForm
          open={isPerformanceModalOpen}
          onClose={() => setIsPerformanceModalOpen(false)}
          dateID={dateID}
          overlayZIndexClass="z-[10001]"
          onSubmit={(performanceLog) => {
            setPendingPerformance({
              notes: performanceLog.notes,
              performanceExpectation: performanceLog.performanceExpectation,
              performanceResult: performanceLog.performanceResult,
              performanceTitle: performanceLog.performanceTitle,
              performanceDate: performanceLog.performanceDate,
              activity: performanceLog.activity,
              tag: performanceLog.tag,
            });
          }}
        />
      )}
    </>
  );
};

export default TrainingLogForm;
